import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { ActionButtonVariants } from 'components/action-button';
import Modal, { ModalSize } from 'components/modal';
import { ExternalOfferModalDetails } from 'state/loyalty/types';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { blockContentToPlainText } from 'utils/sanity';

import {
  CTAButton,
  CloseButton,
  Container,
  ImageWrapper,
  MainTitle,
  OfferDescription,
  OfferImage,
  TextContentWrapper,
} from './external-incentive-modal.styled';

interface ModalExternalOfferRedeemProps {
  onChangeVisibility: VoidFunction;
  modalVisibility?: boolean | undefined;
  modalDetails: ExternalOfferModalDetails;
}

const ModalContent = ({ onChangeVisibility, modalDetails }: ModalExternalOfferRedeemProps) => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const location = useLocation();
  const mParticle = useMParticleContext();
  const now = useMemo(() => new Date().toString(), []);

  const onCTAClickHandler = useCallback(() => {
    mParticle.trackEvent({
      name: CustomEventNames.EXTERNAL_OFFER_REDEEMED,
      type: EventTypes.Other,
      attributes: {
        sanityId: modalDetails.sanityId,
        name: modalDetails.title,
        createdAt: now,
      },
    });

    const navigationURL = modalDetails.buttonsConfiguration?.firstButton?.buttonPath?.locale;
    if (!navigationURL) {
      return;
    }

    onChangeVisibility();

    if (navigationURL.indexOf('http://') === 0 || navigationURL.indexOf('https://') === 0) {
      window.location.href = navigationURL;
    } else {
      navigate(navigationURL);
    }
  }, [
    navigate,
    onChangeVisibility,
    modalDetails.buttonsConfiguration?.firstButton?.buttonPath?.locale,
    mParticle,
  ]);

  return (
    <Container>
      <ImageWrapper>
        <OfferImage image={modalDetails?.image?.locale} alt="" objectFitContain />
      </ImageWrapper>
      <TextContentWrapper>
        <MainTitle>{modalDetails?.externalOfferModal?.title?.locale}</MainTitle>
        <OfferDescription>
          {blockContentToPlainText(modalDetails?.externalOfferModal?.description?.localeRaw)}{' '}
        </OfferDescription>
        <CTAButton
          onClick={onCTAClickHandler}
          aria-label="confirm-external-incentive-link"
          data-testid="confirm-external-incentive-link"
          href={modalDetails?.buttonsConfiguration?.firstButton?.buttonPath?.locale ?? ''}
          eventAttributes={{
            Name: 'Yes',
            Path: location.pathname,
          }}
        >
          {formatMessage({ id: 'yes' })}
        </CTAButton>
        <CloseButton
          onClick={() => onChangeVisibility()}
          aria-label="close-external-incentive-modal"
          data-testid="close-external-incentive-modal"
          variant={ActionButtonVariants.TEXT_ONLY}
          eventAttributes={{
            Name: 'Not Now',
            Path: location.pathname,
          }}
        >
          {formatMessage({ id: 'notNow' })}
        </CloseButton>
      </TextContentWrapper>
    </Container>
  );
};

const ExternalIncentiveModal = ({
  onChangeVisibility: onChangeExternalOfferModalVisibility,
  modalVisibility,
  modalDetails,
}: ModalExternalOfferRedeemProps) => {
  const location = useLocation();
  if (!modalVisibility) {
    return null;
  }

  return (
    <Modal
      data-testid="external-offer-redeem-modal"
      backgroundColor="transparent"
      onDismiss={() => onChangeExternalOfferModalVisibility()}
      allowsDismiss={false}
      mParticleEventData={{
        Name: 'Access External Offer',
        Path: location.pathname,
        modalAppearanceEventMessage: 'Modal External Offers Redeem',
        modalMessage: 'Modal External Offers Redeem',
      }}
      size={ModalSize.AUTO}
      overrideStyles={{
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <ModalContent
        onChangeVisibility={onChangeExternalOfferModalVisibility}
        modalDetails={modalDetails}
      />
    </Modal>
  );
};

export default ExternalIncentiveModal;
